import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Switch,
  Paper
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { callAPI, Method, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { WSFullScreenLoader } from "../../../Components/Loader/WSLoadingOverlay";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { modules, permissionList, roles } from ".";

const RolePermission = () => {

 useEffect(() => {
    updateMainHeader("Administration");
  }, []);
  
  const { showMessage } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<any>({});
  const [expanded, setExpanded] = useState<string | false>("Inventory");
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      try {
        const response = await callAPI("permissions/list", Method.GET);
        const data = response.data.results;

        const permObj: any = {};

        data.forEach((item: any) => {
          const module = item.Module.toLowerCase();
          const role = item.Role;
          const action = item.Action;
          const isActive = item.IsActive;

          if (!permObj[module]) permObj[module] = {};
          if (!permObj[module][role]) permObj[module][role] = {};

          permObj[module][role][action] = isActive;
        });

        // Fill missing actions as false
        modules.forEach((section) => {
          section.children.forEach((mod) => {
            roles.forEach((role) => {
              if (!permObj[mod.value]) permObj[mod.value] = {};
              if (!permObj[mod.value][role])
                permObj[mod.value][role] = {};

              permissionList.forEach((perm) => {
                if (
                  permObj[mod.value][role][perm.value] === undefined
                ) {
                  permObj[mod.value][role][perm.value] = false;
                }
              });
            });
          });
        });

        setPermissions(permObj);
      } catch (err) {
        console.error("Failed to fetch permissions", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  const isModuleEnabled = (moduleValue: string, role: string) => {
    const actions = permissions[moduleValue]?.[role];

    if (!actions) return false;

    return permissionList.every(
      (perm) => actions[perm.value] === true
    );
  };

  const handleAccordionChange =
    (panel: string) => (_: any, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleToggleAll = (moduleValue: string, role: string, value: boolean) => {
    permissionList.forEach((perm) => {
      handleToggle(moduleValue, role, perm.value);
    });
  };

  const handleModuleToggle = async (
    moduleValue: string,
    role: string
  ) => {
    const isEnabled = isModuleEnabled(moduleValue, role);
    const newState = !isEnabled;

    // Optimistic UI update
    setPermissions((prev: any) => {
      const updatedActions: any = {};

      permissionList.forEach((perm) => {
        updatedActions[perm.value] = newState;
      });

      return {
        ...prev,
        [moduleValue]: {
          ...prev[moduleValue],
          [role]: {
            ...prev[moduleValue]?.[role],
            ...updatedActions,
          },
        },
      };
    });

    try {
      // Call API for each action
      await Promise.all(
        permissionList.map((perm) =>
          callAPI("permissions/", Method.PATCH, {
            role,
            module: moduleValue,
            action: perm.value,
            is_active: newState,
          })
        )
      );

      showMessage("Permissions updated successfully", "success");
    } catch (err) {
      console.error("Failed module toggle", err);
    }
  };

  const handleToggle = async (
    moduleValue: string,
    role: string,
    action: string
  ) => {
    const currentState =
      permissions[moduleValue]?.[role]?.[action] || false;

    const newState = !currentState;

    // Optimistic UI
    setPermissions((prev: any) => ({
      ...prev,
      [moduleValue]: {
        ...prev[moduleValue],
        [role]: {
          ...prev[moduleValue]?.[role],
          [action]: newState,
        },
      },
    }));
 
    try {
      let response = await callAPI(
        "permissions/",
        Method.PATCH,
        {
          role,
          module: moduleValue,
          action,
          is_active: newState,
        }
      );
      showMessage(response.message || "Permission updated successfully", "success");

    } catch (err) {
      console.error("Failed to update permission", err);

      // rollback
      setPermissions((prev: any) => ({
        ...prev,
        [moduleValue]: {
          ...prev[moduleValue],
          [role]: {
            ...prev[moduleValue]?.[role],
            [action]: currentState,
          },
        },
      }));
    }
  };

  return (
    <Box p={3}>
      { }
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          Role Permission
        </Typography>
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        { }
        <Grid
          container
          sx={{
            background: "#2c2c2c",
            color: "#fff",
            p: 1.5,
            fontWeight: 600,
          }}
        >
          <Grid item xs={4}>
            Module / Feature
          </Grid>
          {roles.map((role) => (
            <Grid item xs={2} key={role} textAlign="center">
              {role}
            </Grid>
          ))}
        </Grid>

        { }
        {modules.map((section) => (
          <Accordion
            key={section.name}
            expanded={expanded === section.name}
            onChange={handleAccordionChange(section.name)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{section.name}</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ pl: 2 }}>
              <Box sx={{ position: "relative", ml: 2 }}>
                { }
                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: "#dcdcdc",
                  }}
                />

                {section.children.map((module) => {
                  const uniqueKey = `${section.name}-${module.value}`;

                  return (
                    <Box key={uniqueKey} sx={{ position: "relative" }}>
                      <Grid container alignItems="center" sx={{ py: 1 }}>
                        <Grid item xs={4}>
                          <Box
                            onClick={() =>
                              setExpandedModule((prev) =>
                                prev === uniqueKey ? null : uniqueKey
                              )
                            }
                            sx={{
                              ml: 4,
                              border: "1px solid #bbb",
                              borderRadius: "20px",
                              px: 2,
                              py: 0.5,
                              display: "inline-block",
                              fontSize: 13,
                              cursor: "pointer",
                              background:
                                expandedModule === uniqueKey ? "#000" : "#f9f9f9",
                              color: expandedModule === uniqueKey ? "#fff" : "#000",
                            }}
                          >
                            {module.label}
                          </Box>
                        </Grid>

                        {roles.map((role) => (
                          <Grid item xs={2} key={role} textAlign="center">
                            <Switch
                              checked={isModuleEnabled(module.value, role)}
                              onChange={() => handleModuleToggle(module.value, role)}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#00c853",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: "#00c853",
                                },
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>

                      {expandedModule === uniqueKey && (
                        <Box sx={{ ml: 6, mt: 1, mb: 2 }}>
                          {permissionList.map((perm) => (
                            <Box key={perm.value} sx={{ mb: 1 }}>
                              <Typography fontSize={14} mb={0.5}>
                                {perm.label}
                              </Typography>

                              <Grid container>
                                {roles.map((role) => (
                                  <Grid item xs={2} key={role} textAlign="center">
                                    <Switch
                                      size="small"
                                      checked={
                                        permissions[module.value]?.[role]?.[perm.value] || false
                                      }
                                      onChange={() =>
                                        handleToggle(module.value, role, perm.value)
                                      }
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <WSFullScreenLoader loading={loading} />
    </Box>
  );
};

export default RolePermission;